import Immutable from 'immutable';

import type ErrorData from 'sharedreact/data/ErrorData';
import type Filter from 'sharedreact/data/Filter';
import GroupAndCount from 'sharedreact/data/GroupAndCount';
import SortOrder from 'sharedreact/data/SortOrder';
import User from 'sharedreact/data/User';
import UserWithPassword from 'sharedreact/data/UserWithPassword';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import ConnectionSupport from 'sharedreact/utils/ConnectionSupport';
import FilterSupport from 'sharedreact/utils/FilterSupport';

type OnFetchSuccess = (
  range: RangeType,
  totalCount: number,
  items: Immutable.List<User>,
  countPerGroup: Immutable.List<GroupAndCount>,
  filter: Filter,
  advancedFilter: Filter,
  useOnlyNamesDefaultFilter: boolean | null,
  groupBy: SortOrder,
  sortOrder: SortOrder
) => void;
type OnFetchError = (range: RangeType, error: ErrorData) => void;

type OnFetchSuggestionsSuccess = (users: Immutable.List<User>, totalCount: number) => void

type OnFindSuccess = (indexes: Immutable.Map<string, number>) => void;
type OnFindError = (ids: Immutable.Set<string>, error: ErrorData) => void;

type OnGetByIdSuccess = (items: Immutable.List<User>) => void;
type OnGetByIdError = (ids: string[], error: ErrorData) => void;

type OnSaveSuccess = (item: User) => void;
type OnSaveError = (item: User, error: ErrorData) => void;

type OnRemoveSuccess = (ids: Immutable.Set<string>) => void;
type OnRemoveError = (ids: Immutable.Set<string>, error: ErrorData) => void;

type OnError = (error: ErrorData) => void;

const _filterKeywords = ['username', 'fullname', 'email'];

function fetch (
  range: RangeType,
  filter: Filter,
  advancedFilter: Filter,
  useOnlyNamesDefaultFilter: boolean | null,
  groupBy: SortOrder,
  sortOrder: SortOrder,
  onSuccess: OnFetchSuccess,
  onError: OnFetchError
): void {
  const expandedFilter: Filter = FilterSupport.expand(
      filter,
      _filterKeywords
    );

  const expandedAdvancedFilter: Filter = FilterSupport.expand(
      advancedFilter,
      _filterKeywords
    );

  ConnectionSupport.post(
      'getUsers',
    {
      queryData: {
        range: range,
        filter: expandedFilter,
        advancedFilter: expandedAdvancedFilter,
        groupBy: groupBy,
        sortOrder: sortOrder
      },
      useOnlyNamesDefaultFilter: useOnlyNamesDefaultFilter
    },
      data =>
        onSuccess(
          data.range,
          data.totalCount,
          Immutable.List(data.items.map((item: object) => User.fromJS(item))),
          Immutable.List(data.countPerGroup.map(GroupAndCount.fromJS)),
          filter,
          advancedFilter,
          useOnlyNamesDefaultFilter,
          groupBy,
          sortOrder
        ),
      (error: ErrorData) => onError(range, error)
    );
}

function fetchSuggestions (
  filterString: string | null,
  onSuccess: OnFetchSuggestionsSuccess,
  onError: OnError
): void {
  ConnectionSupport.post(
    'getUserSuggestions',
    { filterString: filterString },
    (data) => onSuccess(Immutable.List(data.items.map((item: any) => User.fromJS(item))), data.totalCount),
    onError
  );
}

function getById (
  ids: string[],
  onSuccess: OnGetByIdSuccess,
  onError: OnGetByIdError
): void {
  ConnectionSupport.post(
    'getUsersByID',
    { ids: ids },
    data => onSuccess(Immutable.List(data.items.map((item: object) => User.fromJS(item)))),
    (error: ErrorData) => onError(ids, error)
  );
}

function find (
  ids: Immutable.Set<string>,
  filter: Filter,
  advancedFilter: Filter,
  useOnlyNamesDefaultFilter: boolean | null,
  groupBy: SortOrder,
  sortOrder: SortOrder,
  onSuccess: OnFindSuccess,
  onError: OnFindError
): void {
  const expandedFilter: Filter = FilterSupport.expand(
    filter,
    _filterKeywords
  );

  const expandedAdvancedFilter: Filter = FilterSupport.expand(
    advancedFilter,
    _filterKeywords
  );

  const jsIds = ids.toJS();
  ConnectionSupport.post(
    'findUsers',
    {
      ids: jsIds,
      queryData: {
        filter: expandedFilter,
        advancedFilter: expandedAdvancedFilter,
        groupBy: groupBy,
        sortOrder: sortOrder
      }
    },
    data => {
      let indexes: Immutable.Map<string, number> = Immutable.Map();
      for (let i = 0; i < jsIds.length; i++) {
        indexes = indexes.set(jsIds[i], data.indexes[i]);
      }
      onSuccess(indexes);
    },
    (error: ErrorData) => onError(ids, error)
  );
}

function save (item: UserWithPassword, onSuccess: OnSaveSuccess, onError: OnSaveError): void {
  ConnectionSupport.post(
      'saveUser',
      { user: item.toJS() },
      (data) => onSuccess(User.fromJS(data.user)),
      (error: ErrorData) => onError(item.user, error)
    );
}

function remove (ids: Immutable.Set<string>, onSuccess: OnRemoveSuccess, onError: OnRemoveError): void {
  ConnectionSupport.post(
    'removeUsers',
    { ids: ids.toJS() },
    (data) => onSuccess(Immutable.Set(data.userIds)),
    (error: ErrorData) => onError(ids, error)
  );
}

export default {
  fetch,
  fetchSuggestions,
  find,
  getById,
  save,
  remove
};
